import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { ContractDoc } from "@/components/documents/Documents";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Printer, CreditCard, Lock } from "lucide-react";
import { CAD } from "@/lib/constants";
import { toast } from "sonner";

const Centered = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-cream px-6 text-center">{children}</div>
);

export default function SignContract() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [sig, setSig] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = () =>
    api.get(`/public/contract/${token}`)
      .then((r) => { setData(r.data); if (r.data.signed) setDone(true); })
      .catch(() => setErr("This signing link is invalid or has expired. Please contact Drunk Fruit Mobile Bar."));

  useEffect(() => { load(); }, [token]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Please enter your full legal name"); return; }
    if (!sig) { toast.error("Please provide your signature"); return; }
    if (!accepted) { toast.error("Please accept the terms to continue"); return; }
    setSubmitting(true);
    try {
      await api.post(`/public/contract/${token}/sign`, { legal_name: name.trim(), signature_data: sig, signature_type: "electronic", accepted: true, base_url: window.location.origin });
      toast.success("Contract signed — thank you!");
      await load();
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Signing failed. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (err) return <Centered><div><img src="/logo-mark.png" alt="Drunk Fruit" className="h-14 mx-auto mb-4" /><p className="text-muted-foreground">{err}</p></div></Centered>;
  if (!data) return <Centered><p className="text-muted-foreground animate-pulse tracking-widest uppercase text-sm">Loading contract…</p></Centered>;

  const ev = data.event;
  const q = ev.quote || {};
  const squareLink = ev.invoice?.square_link;
  const depositPaid = ["Deposit paid", "Balance pending", "Fully paid"].includes(ev.payment_status);

  return (
    <div className="min-h-screen bg-cream py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center gap-3 mb-6 no-print">
          <img src="/logo-mark.png" alt="Drunk Fruit" className="h-12 w-auto" />
          <div><div className="font-serif text-2xl leading-none">Service Agreement</div><div className="text-xs text-muted-foreground">Please review and sign below</div></div>
        </div>

        {done && (
          <div className="df-card p-8 text-center mb-6 no-print" data-testid="sign-success">
            <CheckCircle2 className="h-14 w-14 text-olive mx-auto mb-4" />
            <h2 className="font-serif text-3xl mb-2">Thank you{ev.contract?.signature?.legal_name ? `, ${ev.contract.signature.legal_name}` : ""}!</h2>
            <p className="text-muted-foreground mb-6">Your contract has been signed and sent to Drunk Fruit Mobile Bar. A copy is shown below — you can print or save it for your records.</p>
            <button onClick={() => window.print()} className="btn-dark inline-flex" data-testid="sign-print"><Printer className="h-4 w-4" /> Print / Save Copy</button>
          </div>
        )}

        <div className="print-area rounded-2xl overflow-hidden shadow-lg mb-6">
          <ContractDoc e={ev} pricing={data.pricing} />
        </div>

        {!done && (
          <div className="df-card p-6 no-print" data-testid="sign-panel">
            <h3 className="font-serif text-2xl mb-4">Sign this agreement</h3>
            <div className="mb-4"><Label className="df-label">Full legal name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="df-input" placeholder="Your full legal name" data-testid="sign-name" /></div>
            <div className="mb-4"><Label className="df-label">Signature</Label><SignaturePad onChange={setSig} /></div>
            <label className="flex items-start gap-3 mb-5 text-sm cursor-pointer" data-testid="sign-accept">
              <Checkbox checked={accepted} onCheckedChange={(v) => setAccepted(!!v)} className="mt-0.5" />
              <span>I, {name || "the client"}, have read and agree to the terms of this service agreement, including the alcohol responsibility, cancellation, and payment terms.</span>
            </label>
            <Button onClick={submit} disabled={submitting} className="btn-dark w-full h-12" data-testid="sign-submit">{submitting ? "Submitting…" : "Accept & Sign"}</Button>
            <p className="text-[11px] text-muted-foreground mt-3">By signing, you agree this electronic signature is legally binding. Your name, email, signature, date/time and IP address will be recorded.</p>
          </div>
        )}

        {/* 50% DEPOSIT — locked until the contract is signed */}
        <div className="df-card p-6 no-print mt-6" data-testid="deposit-section">
          <h3 className="font-serif text-2xl mb-1 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-olive" /> 50% Deposit{q.deposit != null ? ` · ${CAD(q.deposit)}` : ""}
          </h3>
          {depositPaid ? (
            <p className="text-sm text-olive flex items-center gap-2 mt-2" data-testid="deposit-paid-msg">
              <CheckCircle2 className="h-4 w-4" /> Your deposit has been received — thank you! Your event date is reserved.
            </p>
          ) : !done ? (
            <>
              <p className="text-sm text-muted-foreground mt-1 mb-4" data-testid="deposit-lock-msg">
                For security and booking protection, your signature is required before the invoice and payment link become available.
              </p>
              <Button disabled className="btn-dark opacity-50 cursor-not-allowed" data-testid="deposit-locked-btn">
                <Lock className="h-4 w-4" /> Pay 50% Deposit (sign first)
              </Button>
            </>
          ) : squareLink ? (
            <>
              <p className="text-sm text-muted-foreground mt-1 mb-4">Thank you for signing. You can now pay your 50% deposit to reserve your event date.</p>
              <a href={squareLink} target="_blank" rel="noreferrer" className="btn-dark inline-flex" data-testid="deposit-pay-btn">
                <CreditCard className="h-4 w-4" /> Pay 50% Deposit{q.deposit != null ? ` · ${CAD(q.deposit)}` : ""}
              </a>
            </>
          ) : (
            <p className="text-sm text-muted-foreground mt-2" data-testid="deposit-pending-link">
              Thank you for signing. Drunk Fruit will send you a secure payment link shortly to complete your 50% deposit.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
